import { trackEvent, scheduleTrackEvent } from "../../analytics/analytics.js";
import {
  FX_CURRENCY_ANALYTICS_EVENTS,
} from "../../constants/fxCurrencyAnalyticsConstants.js";
import { buildFinanceFxPair } from "../../constants/financeFxConstants.js";
import { isConvertedDisplayCurrency, STORAGE_CURRENCY } from "../../intl/fxDisplayCopy.js";
import { resolveCurrentFxRate } from "./buyerFxHistoryFormatters.js";

function normalizeRate(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return numeric;
}

/**
 * @param {object} [options]
 * @returns {object}
 */
export function buildFxSnapshotAnalyticsPayload({
  displayCurrency,
  previousCurrency = null,
  rates = null,
  ratesMeta = null,
  ratesAvailable = true,
  surface = null,
  sourceComponent = null,
  historyPointCount = null,
  historyDays = null,
  pair = null,
} = {}) {
  const convertedDisplay = isConvertedDisplayCurrency(displayCurrency);
  const displayRate = convertedDisplay
    ? normalizeRate(resolveCurrentFxRate({ displayCurrency, rates }))
    : null;
  const resolvedPair = pair || buildFinanceFxPair(displayCurrency);

  return {
    surface,
    sourceComponent,
    storageCurrency: STORAGE_CURRENCY,
    displayCurrency,
    previousCurrency: previousCurrency || null,
    pair: resolvedPair,
    convertedDisplay,
    ratesAvailable: ratesAvailable !== false,
    fxSource: ratesMeta?.source || null,
    fxStale: Boolean(ratesMeta?.stale),
    fxIsFallback: Boolean(ratesMeta?.isFallback),
    fxSnapshotId: ratesMeta?.snapshotId ?? null,
    fxEffectiveDate: ratesMeta?.effectiveDate || ratesMeta?.asOf || null,
    fxDisplayRate: displayRate,
    ...(historyPointCount != null ? { fxHistoryPointCount: historyPointCount } : {}),
    ...(historyDays != null ? { fxHistoryDays: historyDays } : {}),
  };
}

export function trackCurrencyChanged({
  fromCurrency,
  toCurrency,
  rates = null,
  ratesMeta = null,
  ratesAvailable = true,
  sourceComponent = null,
  surface = null,
} = {}) {
  if (!toCurrency || fromCurrency === toCurrency) return null;

  return trackEvent(
    FX_CURRENCY_ANALYTICS_EVENTS.CURRENCY_CHANGED,
    buildFxSnapshotAnalyticsPayload({
      displayCurrency: toCurrency,
      previousCurrency: fromCurrency,
      rates,
      ratesMeta,
      ratesAvailable,
      sourceComponent,
      surface: surface || "currency_change",
    })
  );
}

export function trackFxSnapshotViewed({
  displayCurrency,
  rates = null,
  ratesMeta = null,
  ratesAvailable = true,
  surface = null,
  sourceComponent = null,
  historyPointCount = null,
  historyDays = null,
  pair = null,
  schedule = false,
} = {}) {
  if (!isConvertedDisplayCurrency(displayCurrency)) return null;

  const payload = buildFxSnapshotAnalyticsPayload({
    displayCurrency,
    rates,
    ratesMeta,
    ratesAvailable,
    surface,
    sourceComponent,
    historyPointCount,
    historyDays,
    pair,
  });

  if (schedule) {
    scheduleTrackEvent(FX_CURRENCY_ANALYTICS_EVENTS.FX_SNAPSHOT_VIEWED, payload);
    return payload;
  }

  return trackEvent(FX_CURRENCY_ANALYTICS_EVENTS.FX_SNAPSHOT_VIEWED, payload);
}
